import type { Transition } from "framer-motion"
import * as React from "react"
import { cx } from "./cx"
import { useAddVariantProps, VariantProps } from "./useAddVariantProps"
import { useForceUpdate } from "./useForceUpdate"

function createGestureVariant(variant: string, type: "hover" | "pressed") {
    return `${variant}-${type}`
}

function nextVariant(allVariants: string[], currentVariant: string): string {
    const index = allVariants.indexOf(currentVariant)
    let nextIndex = index + 1
    if (nextIndex >= allVariants.length) {
        nextIndex = 0
    }
    return allVariants[nextIndex]
}

type GestureState = Partial<{
    isHovered: boolean
    isPressed: boolean
}>

// EnabledGestures and EnabledVariantGestures are mirrored from Framer in VariantsStore.ts
interface EnabledGestures {
    hover: boolean
    pressed: boolean
}

type EnabledVariantGestures = Record<string, EnabledGestures>
type VariantNames = string[]
interface VariantState {
    variants: VariantNames
    baseVariant: string | undefined
    gestureVariant: string | undefined
    classNames: string
    transition: Partial<Transition> | undefined
    setVariant: (variant: string | typeof CycleVariantState) => void
    setGestureState: (gestureState: GestureState) => void
    addVariantProps?: (id: string) => Record<string, unknown>
}

/**
 * @internal
 */
export const enum VariantSelector {
    Variant = "v",
}

function getGesture(
    enabledGestures: EnabledGestures | undefined,
    isHovered: boolean,
    isPressed: boolean
): "hover" | "pressed" | void {
    const { hover, pressed } = enabledGestures || {}
    if (pressed && isPressed) return "pressed"
    if (hover && isHovered) return "hover"
}

function createVariantClassName(baseVariant: string, variantClassNames: Record<string, string>) {
    const mappedClassName = variantClassNames[baseVariant]
    if (mappedClassName) return mappedClassName

    return `framer-${VariantSelector.Variant}-${baseVariant}`
}

interface InternalState {
    isHovered: boolean
    isPressed: boolean
    baseVariant: string
    defaultVariant: string
    gestureVariant?: string
    enabledGestures?: EnabledVariantGestures
    cycleOrder?: string[]
    transitions?: Record<string, Partial<Transition>>
}

/**
 * Flag setVariantState as cycling variants.
 * @public
 */
export const CycleVariantState = Symbol("cycle")

/**
 * Handle stateful logic in Framer Canvas Components.
 *
 * @internal
 */
export function useVariantState({
    variant,
    defaultVariant: externalDefaultVariant,
    transitions: externalTransitions,
    enabledGestures: externalEnabledGestures,
    cycleOrder: externalCycleOrder = [],
    variantProps = {},
    variantClassNames = {},
}: {
    defaultVariant: string
    cycleOrder: string[]
    variant?: string
    transitions?: Record<string, Partial<Transition>>
    enabledGestures?: EnabledVariantGestures
    variantProps?: VariantProps
    variantClassNames?: Record<string, string>
}): VariantState {
    const forceUpdate = useForceUpdate()

    const internalState = React.useRef<InternalState>({
        isHovered: false,
        isPressed: false,
        baseVariant: variant ?? externalDefaultVariant,
        gestureVariant: undefined,

        // When used in generated components, these are static values defined
        // outside of the component function that also need to not result in
        // memoized values being recalculated, so we dump them into the ref.
        defaultVariant: externalDefaultVariant,
        enabledGestures: externalEnabledGestures,
        cycleOrder: externalCycleOrder,
        transitions: externalTransitions,
    })

    const resolveNextVariant = React.useCallback(
        (nextBaseVariant: string | undefined = internalState.current.defaultVariant) => {
            const {
                baseVariant,
                gestureVariant,
                isPressed,
                isHovered,
                defaultVariant,
                enabledGestures,
            } = internalState.current

            const gesture = getGesture(enabledGestures?.[nextBaseVariant], isHovered, isPressed)
            const nextGestureVariant = gesture ? createGestureVariant(nextBaseVariant, gesture) : undefined

            // Only force a render if the new active variants have changed.
            if (nextBaseVariant !== baseVariant || nextGestureVariant !== gestureVariant) {
                internalState.current.baseVariant = nextBaseVariant || defaultVariant
                internalState.current.gestureVariant = nextGestureVariant
                forceUpdate()
            }
        },
        [forceUpdate]
    )

    const setGestureState: VariantState["setGestureState"] = React.useCallback(
        ({ isHovered, isPressed }) => {
            if (isHovered !== undefined) internalState.current.isHovered = isHovered
            if (isPressed !== undefined) internalState.current.isPressed = isPressed

            resolveNextVariant(internalState.current.baseVariant)
        },
        [resolveNextVariant]
    )

    const setVariant = React.useCallback(
        (proposedVariant?: typeof CycleVariantState | string) => {
            const { defaultVariant, cycleOrder, baseVariant } = internalState.current
            const nextBaseVariant =
                proposedVariant === CycleVariantState
                    ? nextVariant(cycleOrder || [], baseVariant || defaultVariant)
                    : proposedVariant

            resolveNextVariant(nextBaseVariant || defaultVariant)
        },
        [resolveNextVariant]
    )

    React.useLayoutEffect(() => {
        if (variant !== internalState.current.baseVariant) setVariant(variant)
    }, [variant, setVariant])

    const { baseVariant } = internalState.current
    const transition = React.useMemo(() => {
        const { transitions } = internalState.current
        if (!transitions) return undefined

        if (baseVariant) {
            const variantTransition = transitions[baseVariant]
            if (variantTransition) return variantTransition
        }

        return transitions.default
    }, [baseVariant])

    const variants = []
    const { gestureVariant, defaultVariant, enabledGestures, isHovered, isPressed } = internalState.current
    if (baseVariant && baseVariant !== defaultVariant) variants.push(baseVariant)
    if (gestureVariant) variants.push(gestureVariant)

    // Backwards compatibility for old components generated before
    // addVariantProps was extracted to it's own hook.
    const addVariantProps = useAddVariantProps(baseVariant, gestureVariant, variantProps)

    return {
        variants,
        baseVariant,
        gestureVariant,
        transition,
        setVariant,
        setGestureState,
        addVariantProps,
        classNames: cx(
            createVariantClassName(baseVariant, variantClassNames),
            getGesture(enabledGestures?.[baseVariant], isHovered, isPressed)
        ),
    }
}
